import { useMemo } from 'react';
import MantineTable from '../../../../../common/table/mantine';
import { Status } from '../../../styles';
import { Button, NumberInput, Loader, Text, Stack } from '@mantine/core';
import moment from 'moment';
import { IconRefresh } from '@tabler/icons-react';
import AssignedUsersList from '../../../../../components/assignedUsersList';

const FinalizeTable = ({
  data,
  loading,
  initialState,
  handleChange,
  setLogsModal,
  finalizeTp,
  submitLogisticsChange,
  checkAccess,
  fetch,
  syncDetails,
}) => {
  const isSyncing = (data) => {
    const { status, flag, indicative_database } = data;
    if (['Closed', 'Not Started'].includes(status)) return false;
    if (indicative_database.includes('out-zone') && !flag) return true;
    if (['In Progress'].includes(status) && !flag && !indicative_database.includes('out-zone'))
      return true;

    return false;
  };

  const columns = useMemo(() => [
    {
      accessorKey: 'control_number',
      header: 'Control Number',
    },
    {
      accessorKey: 'updated_by',
      header: 'Updated By',
    },
    {
      accessorKey: 'producing_zone',
      header: 'Producing Zone',
    },
    {
      accessorKey: 'producing_country',
      header: 'Producing Country',
    },
    {
      accessorKey: 'manufacturing_service',
      header: 'Manufacturing Service ',
    },
    {
      accessorKey: 'manufacturing_entity_name',
      header: 'Manufacturing Entity Name',
    },
    {
      accessorKey: 'sales_org',
      header: 'Sales Org(ERP)',
    },
    {
      accessorKey: 'manufacturing_plant',
      header: 'Manufacturing Plant',
    },
    {
      accessorKey: 'buying_zone',
      header: 'Buying Zone',
    },
    {
      accessorKey: 'intermediary_elbe_country',
      header: 'Intermediary Country Elbe(BE)',
    },
    {
      accessorKey: 'indicative_database',
      header: 'Indicative DB',
    },
    {
      accessorKey: 'buying_country',
      header: 'Buying Country',
    },
    {
      accessorKey: 'brand',
      header: 'Brand',
    },
    {
      accessorKey: 'pack_type',
      header: 'Pack Type',
    },
    {
      accessorKey: 'erp_sku_number',
      header: 'ERP SKU nr.',
    },
    {
      accessorKey: 'sku_name',
      header: 'SKU Name',
    },
    {
      accessorKey: 'incoterm',
      header: 'Incoterm',
    },
    {
      accessorKey: 'curr',
      header: 'Curr',
    },
    {
      accessorKey: 'vic',
      header: 'VIC',
      Cell: ({ row }) => {
        const { status, vic } = row.original;
        const syncing = isSyncing(row.original);

        if (status === 'Not Started') {
          return 'N/A';
        }

        if (syncing) {
          return (
            <span>
              <Loader size='xs' color='blue' />
              Syncing...
            </span>
          );
        }

        return vic;
      },
    },
    {
      accessorKey: 'vplc',
      header: 'VPLC',
      Cell: ({ row }) => {
        const { status, vplc } = row.original;
        const syncing = isSyncing(row.original);

        if (status === 'Not Started') {
          return 'N/A';
        }

        if (syncing) {
          return (
            <span>
              <Loader size='xs' color='blue' />
              Syncing...
            </span>
          );
        }

        return vplc;
      },
    },
    {
      accessorKey: 'fob',
      header: 'FOB',
    },
    {
      accessorKey: 'leffe_royalties',
      header: 'Leffe Royalties',
    },
    {
      accessorKey: 'interco_markup_in_perf',
      header: 'VILC interco (in perf)',
      Cell: ({ row }) => {
        const { status, interco_markup_in_perf } = row.original;
        const syncing = isSyncing(row.original);

        if (status === 'Not Started') {
          return 'N/A';
        }

        if (syncing) {
          return (
            <span>
              <Loader size='xs' color='blue' />
              Syncing...
            </span>
          );
        }

        return interco_markup_in_perf;
      },
    },
    {
      accessorKey: 'fixed_costs',
      header: 'Fixed Costs',
      Cell: ({ row }) => {
        const { status, fixed_costs } = row.original;
        const syncing = isSyncing(row.original);

        if (status === 'Not Started') {
          return 'N/A';
        }

        if (syncing) {
          return (
            <span>
              <Loader size='xs' color='blue' />
              Syncing...
            </span>
          );
        }

        return fixed_costs;
      },
    },
    {
      accessorKey: 'dep',
      header: 'Dep',
      Cell: ({ row }) => {
        const { status, dep } = row.original;
        const syncing = isSyncing(row.original);

        if (status === 'Not Started') {
          return 'N/A';
        }

        if (syncing) {
          return (
            <span>
              <Loader size='xs' color='blue' />
              Syncing...
            </span>
          );
        }

        return dep;
      },
    },
    {
      accessorKey: 'royalties',
      header: 'Royalties',
      Cell: ({ row }) => {
        const { status, royalties } = row.original;
        const syncing = isSyncing(row.original);

        if (status === 'Not Started') {
          return 'N/A';
        }

        if (syncing) {
          return (
            <span>
              <Loader size='xs' color='blue' />
              Syncing...
            </span>
          );
        }

        return royalties;
      },
    },
    {
      accessorKey: 'std_logistics_costs',
      header: 'Std Logistics Costs',
      Cell: ({ row }) => {
        const accessAllowed = checkAccess(
          row.original?.status,
          row.original?.producing_country,
          'logistics',
        );
        return (
          <NumberInput
            disabled={
              !(
                String(row.original.indicative_database).toLowerCase().includes('elbe') &&
                accessAllowed
              )
            }
            precision={4}
            min={0.1}
            max={10000000}
            value={parseFloat(row?.original?.std_logistics_costs || 0) || 0}
            onChange={(value) =>
              handleChange({ id: row.original?.id, std_logistics_costs: value || 0 })
            }
          />
        );
      },
    },
    {
      accessorKey: 'submitted_on',
      header: 'Submitted On',
    },
    {
      accessorKey: 'other_intangible_compensation',
      header: 'Other Intangible Compensation',
    },
    {
      accessorKey: 'mu_percent',
      header: 'MU%',
      Cell: ({ row }) => {
        const { status, mu_percent } = row.original;
        const syncing = isSyncing(row.original);

        if (status === 'Not Started') {
          return 'N/A';
        }

        if (syncing) {
          return (
            <span>
              <Loader size='xs' color='blue' />
              Syncing...
            </span>
          );
        }

        return mu_percent;
      },
    },
    {
      accessorKey: 'std_mu',
      header: 'Std MU',
      Cell: ({ row }) => {
        const { status, std_mu } = row.original;
        const syncing = isSyncing(row.original);

        if (status === 'Not Started') {
          return 'N/A';
        }

        if (syncing) {
          return (
            <span>
              <Loader size='xs' color='blue' />
              Syncing...
            </span>
          );
        }

        return std_mu;
      },
    },
    {
      accessorKey: 'interco_markup_out_perf',
      header: 'Interco Markup (out of perf)',
      Cell: ({ row }) => {
        const { status, interco_markup_out_perf } = row.original;
        const syncing = isSyncing(row.original);

        if (status === 'Not Started') {
          return 'N/A';
        }

        if (syncing) {
          return (
            <span>
              <Loader size='xs' color='blue' />
              Syncing...
            </span>
          );
        }

        return interco_markup_out_perf;
      },
    },
    {
      accessorKey: 'total_tp',
      header: 'Total TP',

      Cell: ({ row }) => {
        const { status, total_tp } = row.original;
        const syncing = isSyncing(row.original);

        if (status === 'Not Started') {
          return 'N/A';
        }

        if (syncing) {
          return (
            <span>
              <Loader size='xs' color='blue' />
              Syncing...
            </span>
          );
        }

        return total_tp;
      },
    },
    {
      accessorKey: 'action',
      header: 'Action',
      Cell: ({ row }) => {
        const cost = parseFloat(row?.original?.std_logistics_costs || 0);
        const db = String(row.original.indicative_database).toLowerCase();
        const accessAllowed = checkAccess(row.original?.status, row.original?.producing_country);
        const syncing = isSyncing(row.original);
        return (
          <Button
            disabled={
              syncing || db.includes('elbe') ? !cost || cost <= 0 || !accessAllowed : !accessAllowed
            }
            color='dark'
            onClick={() => {
              finalizeTp(row?.original?.IntCrTpFinalizations?.[0]?.id);
            }}
          >
            Finalize TP
          </Button>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      Cell: ({ row }) => {
        return row.original?.status ? <Status status={row.original?.status} /> : null;
      },
    },
    {
      accessorKey: 'logs1',
      header: 'Logs',
      Cell: ({ row }) => (
        <Button
          color='dark'
          onClick={() =>
            setLogsModal({
              open: true,
              logs: row.original?.IntCrTpFinalizations?.[0]?.IntCrStepStatusLogs,
              type: 'Finalize TP Flow',
            })
          }
        >
          View Logs
        </Button>
      ),
    },
    {
      accessorKey: 'fpna_logistics_sme',
      header: 'FP&A Logistics SME',
      Cell: ({
        row: {
          original: { fpna_logistics_sme },
        },
      }) => <AssignedUsersList users={fpna_logistics_sme} />,
    },
    {
      accessorKey: 'fpna_gcc_sme',
      header: 'FP&A GCC SME',
      Cell: ({
        row: {
          original: { fpna_gcc_sme },
        },
      }) => <AssignedUsersList users={fpna_gcc_sme} />,
    },
    {
      accessorKey: 'completed_on',
      header: 'Completed On',
    },
    {
      accessorKey: 'logs',
      header: 'Submit',
      Cell: ({ row }) => {
        const status = row.original?.status;
        const country = row.original?.producing_country;
        const accessAllowed =
          checkAccess(status, country, 'logistics') &&
          parseFloat(row?.original?.std_logistics_costs || 0) > 0.0;
        const submitAccess =
          accessAllowed && String(row.original.indicative_database).toLowerCase().includes('elbe');
        return (
          <Button
            disabled={!submitAccess}
            color='dark'
            onClick={() => {
              submitLogisticsChange(
                row?.original?.IntCrTpFinalizations?.[0]?.id,
                row?.original?.std_logistics_costs,
              );
            }}
          >
            Submit
          </Button>
        );
      },
    },
  ]);

  const rightSideBtns = [
    {
      button: (
        <Button variant='light' color='gray.0' onClick={() => fetch()}>
          <IconRefresh color='black' variant='filled' size={16} />
        </Button>
      ),
    },
    syncDetails?.lastSyncAt
      ? {
          button: (
            <Button variant='light' size='sm' color='blue.3' style={{ cursor: 'default' }}>
              <Text size='xs' c='blue.6' opacity={0.7}>
                Prev. Sync:
                <Text span c='blue.6' fw={600}>
                  {' '}
                  {moment(syncDetails.lastSyncAt).format('h:mm A')}
                </Text>
              </Text>
            </Button>
          ),
        }
      : null,

    syncDetails?.nextSyncAt
      ? {
          button: (
            <Button variant='light' size='sm' color='blue.3' style={{ cursor: 'default' }}>
              <Text size='xs' c='blue.6' opacity={0.7}>
                Next Sync:
                <Text span c='blue.6' fw={600}>
                  {' '}
                  {moment(syncDetails.nextSyncAt).format('h:mm A')}
                </Text>
              </Text>
            </Button>
          ),
        }
      : null,
  ];

  return (
    <>
      <MantineTable
        columns={columns}
        initialData={data}
        enablePinning
        initialState={initialState}
        unique={'indexId'}
        enableRowSelection={false}
        loading={loading}
        showSelectedToggle={false}
        hideSelectColumn={true}
        showResetAll={false}
        enableExport={true}
        pageSize={10}
        enableSelectAll={false}
        csvFilename={`finalize_tp_flow_${moment().format('YYYY-MM-DD')}`}
        rightButtons={rightSideBtns.filter((item) => item)}
      />
    </>
  );
};

export default FinalizeTable;
